import React, {Component, ErrorInfo, ReactNode} from 'react';
import * as Sentry from '@sentry/react';
import {Button} from '../ui/buttons/button';
import {Trans} from '../i18n/trans';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error: Error): State {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry if available
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
        },
      });
    }

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-alt p-24">
          <div className="max-w-md text-center">
            <h1 className="mb-16 text-3xl font-bold text-main">
              <Trans message="Something went wrong" />
            </h1>
            <p className="mb-24 text-muted">
              <Trans message="We're sorry, but something unexpected happened. Please try refreshing the page or go back to the homepage." />
            </p>
            <div className="flex items-center justify-center gap-12">
              <Button variant="flat" color="primary" onClick={this.handleReload}>
                <Trans message="Refresh Page" />
              </Button>
              <Button variant="outline" onClick={this.handleGoHome}>
                <Trans message="Go to Homepage" />
              </Button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <div className="mt-24 rounded border border-danger/20 bg-danger/5 p-16 text-left">
                <p className="mb-8 font-mono text-sm font-bold text-danger">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                <pre className="overflow-auto whitespace-pre-wrap font-mono text-xs text-muted">
                  {this.state.error.stack}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
